import React, { useMemo, useState } from 'react';
import { ButtonFilled } from 'components/ui/Button';
import { createWalletAddressSchema, looksValidLength } from 'validation/wallet';
import {
  PolygonGlyph,
  SolanaGlyph,
} from 'components/CreatingTournament/ChainSelector/ChainGlyphs';

import pasteIcon from 'assets/icons/paste-icon.svg';

import {
  FormContainer,
  Input,
  InputWrapper,
  ChainPrefix,
  CopyIcon,
  ErrorText,
  SuccessText,
  ButtonRow,
  CancelButton,
  FormTitle,
  FormSubtitle,
  ButtonWrapper,
  CopyIconImage,
} from './styled';

type ChainKey = 'polygon' | 'solana';

interface WalletAddressFormProps {
  onSubmit: (address: string) => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  /** Chain the tournament runs on; drives ticker label, copy and validation. */
  chain?: ChainKey;
}

const SenderEm: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ color: '#C7C5FF' }}>{children}</span>
);

const WalletAddressForm: React.FC<WalletAddressFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
  chain = 'polygon',
}) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [error, setError] = useState('');

  const ticker = chain === 'solana' ? 'SOL' : 'POL';
  const networkName = chain === 'solana' ? 'Solana' : 'Polygon';
  const ChainGlyph = chain === 'solana' ? SolanaGlyph : PolygonGlyph;

  // Yup validation runs on submit. The chain-specific schema picks accepted
  // address formats: Solana tournaments accept Solana base58 only; Polygon
  // tournaments accept Polygon EVM hex only (regardless of host).
  const validationSchema = useMemo(
    () => createWalletAddressSchema({ allowEvm: chain === 'polygon' }),
    [chain],
  );

  const looksValid = looksValidLength(walletAddress, chain);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await validationSchema.validate({ walletAddress });
      await onSubmit(walletAddress);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWalletAddress(e.target.value);
    if (error) setError('');
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setWalletAddress(text.trim());
      if (error) setError('');
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    }
  };

  const filled = walletAddress.trim().length > 0;

  return (
    <FormContainer>
      <FormTitle>
        Paste <SenderEm>your sender wallet</SenderEm> address
      </FormTitle>
      <FormSubtitle>
        Paste the address of <strong>YOUR wallet</strong> on {networkName} — we'll match the
        incoming payment from this address.
      </FormSubtitle>

      <form onSubmit={handleSubmit}>
        <InputWrapper filled={filled} hasError={!!error} isValid={looksValid && !error}>
          <ChainPrefix aria-label={`${networkName} address`}>
            <ChainGlyph size={14} />
            {ticker}
          </ChainPrefix>
          <Input
            type="text"
            value={walletAddress}
            onChange={handleInputChange}
            placeholder="Wallet address…"
            spellCheck={false}
            autoComplete="off"
            disabled={loading}
            hasError={!!error}
          />
          <CopyIcon type="button" onClick={handlePaste} disabled={loading} aria-label="Paste from clipboard">
            <CopyIconImage src={pasteIcon} alt="" />
            Paste
          </CopyIcon>
        </InputWrapper>
        {error ? (
          <ErrorText>{error}</ErrorText>
        ) : looksValid ? (
          <SuccessText>
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 8.5l3.2 3.2L13 5"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Address looks valid
          </SuccessText>
        ) : null}

        <ButtonRow>
          <ButtonWrapper>
            <CancelButton type="button" onClick={onCancel} disabled={loading}>
              Cancel
            </CancelButton>
          </ButtonWrapper>
          <ButtonWrapper>
            <ButtonFilled
              type="submit"
              disabled={loading || !looksValid}
              style={{ width: '100%' }}
            >
              {loading ? 'Processing...' : 'Continue'}
            </ButtonFilled>
          </ButtonWrapper>
        </ButtonRow>
      </form>
    </FormContainer>
  );
};

export default WalletAddressForm;
